import { collectionKey, frozenClone } from "../presets/preset-internals.js";

const METHODS = Object.freeze([
  "load",
  "create",
  "update",
  "rename",
  "remove",
  "importMany",
  "reset",
]);

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function unavailable(key, operation, reason) {
  return frozenClone({
    kind: "unavailable",
    collectionKey: key,
    operation,
    reason,
  });
}

function authorityUnchanged(startingSession, currentSession) {
  if (startingSession.kind === "signedOut") {
    return currentSession?.kind === "signedOut";
  }
  return startingSession.kind === "ready"
    && currentSession?.kind === "ready"
    && startingSession.accountId === currentSession.accountId;
}

function authorityKey(session) {
  return session?.kind === "ready"
    ? `ready:${session.accountId}`
    : String(session?.kind ?? "invalid");
}

export function createAccountAwareSavedPresetRepository({
  localRepository,
  sessionRepository,
  accountProvider,
} = {}) {
  if (
    !hasMethods(localRepository, METHODS)
    || !hasMethods(accountProvider, METHODS)
    || !hasMethods(sessionRepository, ["load", "refresh", "state", "subscribe"])
  ) {
    throw new TypeError(
      "Saved-preset authority requires exact local, account, and session boundaries.",
    );
  }

  let sessionDiscovery = null;
  let disposed = false;
  let visibleAuthority = authorityKey(sessionRepository.state());
  const authorityListeners = new Set();
  const accountRequests = new Set();

  function abortAccountRequests() {
    for (const controller of accountRequests) {
      controller.abort();
    }
    accountRequests.clear();
  }

  const unsubscribeSession = sessionRepository.subscribe((session) => {
    const nextAuthority = authorityKey(session);
    if (nextAuthority === visibleAuthority) return;
    visibleAuthority = nextAuthority;
    abortAccountRequests();
    for (const listener of authorityListeners) {
      try {
        listener();
      } catch (error) {
        globalThis.reportError?.(error);
      }
    }
  });
  if (typeof unsubscribeSession !== "function") {
    throw new TypeError("Account session subscription must return an unsubscribe function.");
  }

  async function discoverSession({ refreshKnown = false, refreshReady = false } = {}) {
    const current = sessionRepository.state();
    const needsDiscovery = refreshKnown
      || ["uninitialized", "loading"].includes(current?.kind)
      || (refreshReady && current?.kind === "ready");
    if (!needsDiscovery) return current;
    if (!sessionDiscovery) {
      sessionDiscovery = Promise.resolve()
        .then(() => (refreshKnown || refreshReady
          ? sessionRepository.refresh()
          : sessionRepository.load()))
        .catch(() => ({
          kind: "unavailable",
          reason: "session-request-failed",
        }))
        .finally(() => {
          sessionDiscovery = null;
        });
    }
    return sessionDiscovery;
  }

  function boundaryFailure(key, operation, session) {
    if (session?.kind === "locked") {
      return unavailable(
        key,
        operation,
        session.reason || "account-locked",
      );
    }
    if (session?.kind === "unavailable") {
      return unavailable(
        key,
        operation,
        session.reason || "session-request-failed",
      );
    }
    return unavailable(key, operation, "account-session-not-ready");
  }

  async function reconcileAccountRejection(key, operation, mutation) {
    const refreshed = await discoverSession({ refreshReady: true });
    if (disposed) {
      return unavailable(key, operation, "saved-preset-repository-disposed");
    }
    if (mutation) {
      return unavailable(key, operation, "account-authority-changed");
    }
    if (refreshed?.kind !== "signedOut") {
      return boundaryFailure(
        key,
        operation,
        refreshed?.kind === "ready"
          ? { kind: "unavailable", reason: "account-authorization-rejected" }
          : refreshed,
      );
    }
    try {
      const result = await localRepository.load(key);
      return disposed
        ? unavailable(key, operation, "saved-preset-repository-disposed")
        : result;
    } catch (_error) {
      return unavailable(
        key,
        operation,
        disposed
          ? "saved-preset-repository-disposed"
          : "local-saved-presets-failed",
      );
    }
  }

  async function invoke(method, rawKey, args, { mutation = false } = {}) {
    const key = collectionKey(rawKey);
    if (disposed) {
      return unavailable(key, method, "saved-preset-repository-disposed");
    }
    const visibleSession = sessionRepository.state();
    const session = await discoverSession({
      refreshReady: mutation && visibleSession?.kind === "ready",
    });
    if (disposed) {
      return unavailable(key, method, "saved-preset-repository-disposed");
    }
    if (session?.kind !== "signedOut" && session?.kind !== "ready") {
      return boundaryFailure(key, method, session);
    }
    if (mutation && !authorityUnchanged(visibleSession, session)) {
      return unavailable(key, method, "account-authority-changed");
    }

    const repository = session.kind === "signedOut"
      ? localRepository
      : accountProvider;
    const requestController = session.kind === "ready"
      ? new AbortController()
      : null;
    if (requestController) accountRequests.add(requestController);
    try {
      let result;
      if (session.kind === "signedOut") {
        result = await repository[method](key, ...args);
      } else if (method === "load") {
        result = await repository.load(session, key, {
          signal: requestController.signal,
        });
      } else {
        const options = args.at(-1);
        result = await repository[method](
          session,
          key,
          ...args.slice(0, -1),
          { ...options, signal: requestController.signal },
        );
      }
      if (disposed) {
        return unavailable(key, method, "saved-preset-repository-disposed");
      }
      if (session.kind === "ready" && result?.kind === "signedOut") {
        return reconcileAccountRejection(key, method, mutation);
      }
      if (!authorityUnchanged(session, sessionRepository.state())) {
        return unavailable(key, method, "account-authority-changed");
      }
      return result;
    } catch (_error) {
      if (disposed) {
        return unavailable(key, method, "saved-preset-repository-disposed");
      }
      return unavailable(
        key,
        method,
        !authorityUnchanged(session, sessionRepository.state())
          ? "account-authority-changed"
          : session.kind === "ready"
            ? "account-saved-presets-failed"
            : "local-saved-presets-failed",
      );
    } finally {
      if (requestController) accountRequests.delete(requestController);
    }
  }

  async function loadLocalSources(rawKey) {
    const key = collectionKey(rawKey);
    if (disposed) {
      return unavailable(key, "load-local-sources", "saved-preset-repository-disposed");
    }
    const session = await discoverSession();
    if (disposed) {
      return unavailable(key, "load-local-sources", "saved-preset-repository-disposed");
    }
    if (session?.kind !== "ready") {
      return frozenClone({ kind: "hidden", collectionKey: key });
    }
    try {
      const result = await localRepository.load(key);
      if (disposed) {
        return unavailable(key, "load-local-sources", "saved-preset-repository-disposed");
      }
      if (!authorityUnchanged(session, sessionRepository.state())) {
        return unavailable(key, "load-local-sources", "account-authority-changed");
      }
      return result;
    } catch (_error) {
      return unavailable(key, "load-local-sources", "local-saved-presets-failed");
    }
  }

  async function updateLocalSource(rawKey, presetId, options) {
    const key = collectionKey(rawKey);
    if (disposed) {
      return unavailable(key, "update-local-source", "saved-preset-repository-disposed");
    }
    try {
      const result = await localRepository.update(key, presetId, options);
      return disposed
        ? unavailable(key, "update-local-source", "saved-preset-repository-disposed")
        : result;
    } catch (_error) {
      return unavailable(
        key,
        "update-local-source",
        disposed
          ? "saved-preset-repository-disposed"
          : "local-saved-presets-failed",
      );
    }
  }

  return Object.freeze({
    refreshAuthority() {
      if (disposed) return Promise.resolve(sessionRepository.state());
      return discoverSession({ refreshKnown: true });
    },
    subscribeAuthority(listener) {
      if (typeof listener !== "function") {
        throw new TypeError("Saved-preset authority listener must be a function.");
      }
      if (disposed) return () => {};
      authorityListeners.add(listener);
      return () => authorityListeners.delete(listener);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      abortAccountRequests();
      unsubscribeSession();
      authorityListeners.clear();
    },
    load(rawKey) {
      return invoke("load", rawKey, []);
    },
    loadLocalSources,
    updateLocalSource,
    create(rawKey, options) {
      return invoke("create", rawKey, [options], { mutation: true });
    },
    update(rawKey, presetId, options) {
      return invoke("update", rawKey, [presetId, options], {
        mutation: true,
      });
    },
    rename(rawKey, presetId, options) {
      return invoke("rename", rawKey, [presetId, options], {
        mutation: true,
      });
    },
    remove(rawKey, presetId, options) {
      return invoke("remove", rawKey, [presetId, options], {
        mutation: true,
      });
    },
    importMany(rawKey, entries, options) {
      return invoke("importMany", rawKey, [entries, options], {
        mutation: true,
      });
    },
    reset(rawKey, options) {
      return invoke("reset", rawKey, [options], { mutation: true });
    },
  });
}
